import type { AnalyticsSeriesPoint } from '@mg/shared';
import { cn } from '../../lib/cn';
// Line definitions live in their own module so this file exports components only — the
// react-refresh lint rule (and HMR) needs that separation.
import { ENGAGEMENT_METRICS, type SeriesMetric, type SparklineMetric } from './sparklineMetrics';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Short weekday label for a UTC YYYY-MM-DD day. */
function weekdayLabel(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? '' : WEEKDAYS[d.getUTCDay()];
}

/**
 * A dependency-free inline SVG line chart for an analytics series. One polyline per entry in
 * `metrics`, all sharing a single y-scale. The SVG scales fluidly to its container (viewBox +
 * width 100%, `preserveAspectRatio="none"` with non-scaling strokes so lines stay crisp);
 * weekday labels render as an aligned HTML row *below* the SVG so they never distort. Handles
 * 0 points (empty gridlines) and 1 point (a flat line) gracefully.
 */
export function Sparkline({
  series,
  metrics = ENGAGEMENT_METRICS,
  height = 96,
  showAxisLabels = true,
  className,
}: {
  series: AnalyticsSeriesPoint[];
  /** Which lines to draw, in back-to-front order. Defaults to engagements + reach. */
  metrics?: SparklineMetric[];
  /**
   * Rendered SVG height in px at full size (width is fluid). Drawn in rem, so the chart shrinks with
   * the rest of the UI where index.css scales it to 80%.
   */
  height?: number;
  /** Render the weekday x-axis labels (only when the range is short enough to read). */
  showAxisLabels?: boolean;
  className?: string;
}) {
  const W = 300;
  const H = 100;
  const padX = 6;
  const padTop = 8;
  const padBottom = 8;
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;

  const n = series.length;
  const maxVal = Math.max(1, ...series.flatMap((p) => metrics.map((m) => p[m.key])));

  const xAt = (i: number) => (n <= 1 ? padX + innerW / 2 : padX + (i / (n - 1)) * innerW);
  const yAt = (v: number) => padTop + innerH - (v / maxVal) * innerH;

  const points = (key: SeriesMetric): string => {
    if (n === 0) return '';
    if (n === 1) {
      const y = yAt(series[0][key]);
      return `${padX},${y} ${W - padX},${y}`;
    }
    return series.map((p, i) => `${xAt(i)},${yAt(p[key])}`).join(' ');
  };

  // Show per-day labels only when the range is short enough to stay legible.
  const labels = showAxisLabels && n >= 2 && n <= 8;

  return (
    <div className={className}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ height: `${height / 16}rem` }}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${metrics.map((m) => m.label).join(', ')} trend`}
      >
        {/* Baseline gridlines at 0 / 50% / 100%. */}
        {[0, 0.5, 1].map((f) => {
          const y = padTop + innerH - f * innerH;
          return (
            <line
              key={f}
              x1={padX}
              x2={W - padX}
              y1={y}
              y2={y}
              className="stroke-line"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {n > 0 &&
          metrics.map((metric) => (
            <polyline
              key={metric.key}
              points={points(metric.key)}
              fill="none"
              className={metric.stroke}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
      </svg>
      {labels && (
        <div className={cn('mt-1 flex justify-between px-1 text-[0.625rem] text-ink-faint')}>
          {series.map((p, i) => (
            <span key={i}>{weekdayLabel(p.day)}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Legend for a {@link Sparkline}, kept here so the swatches can't drift from the line colours. */
export function SparklineLegend({
  metrics,
  className,
}: {
  metrics: SparklineMetric[];
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-4 text-xs text-ink-faint', className)}>
      {metrics.map((metric) => (
        <span key={metric.key} className="inline-flex items-center gap-1.5">
          <span className={cn('inline-block h-0.5 w-4 rounded', metric.swatch)} />
          {metric.label}
        </span>
      ))}
    </div>
  );
}
